/**
 * One monitor state, drawn.
 *
 * The single most load-bearing component in this app, and the one most worth
 * being boring. It renders the state the API stored, in that state's own
 * colour, with that state's own glyph and that state's own word. It computes
 * nothing: `monitorStateFacts` is the client's exhaustive switch over the five
 * labels, and health is inherited from the API's storage layer rather than
 * re-derived in the browser (consumer obligations §10, rule 5).
 *
 * Three properties this deliberately has:
 *
 *  - **Five chips, not three.** `disabled`, `never_run` and `stale` each get
 *    their own colour and glyph. §2 is explicit that one grey dot for all three
 *    fails REQ-105 as surely as a green one does.
 *  - **The word is always present.** Colour is the fast path, never the only
 *    channel — §8 records that no accessibility baseline is specified and flags
 *    this exact model as why that gap matters.
 *  - **The meaning is on the element.** `title` carries the state's `meaning`
 *    string, so "why is this not green?" has an answer on hover rather than in
 *    a document nobody opens. That matters most for `disabled`, which is the
 *    state the eleven-hour outage in §1 was actually in.
 */
import { monitorStateFacts } from "../api/index.ts";
import type { MonitorState } from "../api/index.ts";
import { STATE_COLOURS } from "../theme/state-colours.ts";

export interface StateChipProps {
  readonly state: MonitorState;
}

export function StateChip({ state }: StateChipProps): React.JSX.Element {
  const facts = monitorStateFacts(state);
  const colour = STATE_COLOURS[state];

  return (
    <span
      className="chip"
      title={facts.meaning}
      style={{ color: colour.hex, backgroundColor: colour.tint, borderColor: colour.hex }}
      data-state={state}
    >
      <span className="chip__glyph" aria-hidden="true">
        {colour.glyph}
      </span>
      <span className="chip__label">{facts.label}</span>
    </span>
  );
}
